<template>
  <v-container>
    <v-app-bar color="transparent" flat>
      <div class="title">Cabin Plants</div>
      <v-spacer></v-spacer>
      <v-btn icon large @click="dialog = true"><v-icon>mdi-plus-circle-outline</v-icon></v-btn>
    </v-app-bar>
    <v-row justify="center" wrap dense>
      <v-card class="ma-2" width="300" v-for="plant in plants" :key="plant.id">
        <v-row v-if="plant.images.length" no-gutters>
          <v-col :cols="(plant.images.length / 3) * 4" v-for="image in plant.images" :key="image">
            <v-img :src="image" cover :aspect-ratio="16/9" height="200" />
          </v-col>
        </v-row>
        <v-card-text>
          <div class="title text-truncate">{{ plant.name }}</div>
          <div class="caption">{{ plant.realName }}</div>
          <div>{{ plant.description }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="user.uid === 'd7KzFTtZ5KhqJUVJW8Rxb75Icxm2'" icon @click="deletePlant(plant.id)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-spacer />
          <v-btn v-if="user.uid === plant.authorId" text @click="editPlant(plant)">edit</v-btn>
          <v-btn text @click="seeMore(plant)">see more</v-btn>
        </v-card-actions>
      </v-card>      
    </v-row>
    <v-dialog v-model="dialog" maxWidth="500" scrollable persistent>
      <v-card>
        <v-card-title>
          {{ plant.id ? 'Update plant details' : 'New plant details' }}
        </v-card-title>
        <v-card-text>
          <SlimCropper v-if="ready" ref="cropper" :options="slimOptions" class="cropper">
            <input ref="picker" type="file" name="slim">
          </SlimCropper>
          <v-row>
            <v-col cols="auto" v-for="image in plant.imageURLs" :key="image">
              <v-hover>
                <template v-slot:default="{ hover }">
                  <v-card>
                    <img :src="image" alt="image" apsect-ratio="1" height="50" />
                    <v-fade-transition>
                      <v-overlay v-if="hover" absolute>
                        <v-btn icon @click="removeImage(image)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-overlay>
                    </v-fade-transition>
                  </v-card> 
                </template>
              </v-hover>
            </v-col>
          </v-row>
          <v-form class="mt-4" v-model="valid">
            <v-menu v-model="calendar" :close-on-content-click="false"
                :nudge-right="40" transition="scale-transition" offset-y
                min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field outlined v-model="plant.timestamp" label="Date" readonly v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="plant.timestamp" @input="calendar = false" />
            </v-menu>
            <v-text-field v-model="plant.name" outlined label="Name" />
            <v-text-field v-model="plant.realName" outlined label="Real Name" />
            <v-textarea v-model="plant.description" outlined name="description" label="Description" />
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn outlined @click="cancelDialog">Cancel</v-btn>
          <v-btn depressed color="primary" v-if="plant.id" @click="updatePlant">Update</v-btn>
          <v-btn depressed color="primary" v-else @click="addPlant">Add Plant</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import uuid from 'uuid'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

import { db, storage } from '@/plugins/firebase'
import bus from '@/helpers/bus'
import SlimCropper from '@/components/slimCropper/slim'

let storageRef = storage.ref()

export default {
  components: { SlimCropper },
  data() {
    return {
      calendar: false,
      dialog: false,
      plants: [],
      ready: false,
      valid: null,
      plant: {
        id: null,
        name: '',
        realName: '',
        description: '',
        imageURLs: [],
        timestamp: new Date().toISOString().substr(0, 10)
      },
      slimOptions: {
        label: 'Upload Image',
        size: '640,640',
        service: null,
        serviceFormat: 'file',
        forceType: ['png', 'jpeg', 'jpg'],
        post: ['output'],
        filterSharpen: 15,
        download: true
      }
    }
  },
  filters: {
    date: (d) => {
      return moment(d).format('dddd')
    }
  },
  computed: {
    ...mapState(['user'])
  },
  methods: {
    ...mapActions(['handleFirebaseError']),
    seeMore() {
      bus.$emit('snackbar', 'Hold on this feature is coming soon!!')
    },
    async deletePlant(plantId) {
      try {
        await db.collection('plants').doc(plantId).delete()
      } catch(e) {
        return Promise.reject(e)
      }
    },
    async updatePlant() {
      try {
        await db.collection('plants').doc(this.plant.id).set({
          ...this.plant,
          name: this.plant.name,
          realName: this.plant.realName,
          description: this.plant.description,
          images: this.plant.imageURLs,
          timestamp: new Date(this.plant.timestamp + 'T00:00:00'),
        })

        // reset dialog and close
        this.cancelDialog()
      } catch(e) {
        this.handleFirebaseError(e)
        return Promise.reject(e)
      }
    },
    async addPlant() {
      try {
        if (this.plant.imageURLs.length === 0) {
          await this.$refs.cropper.api.upload()
          while(this.plant.imageURLs.length === 0) {
            await new Promise(r => setTimeout(r, 500))
          }
        }
        await db.collection('plants').add({
          name: this.plant.name,
          realName: this.plant.realName,
          description: this.plant.description,
          images: this.plant.imageURLs,
          timestamp: new Date(this.plant.timestamp + 'T00:00:00'),
          authorId: this.user.uid,
          author: this.user.displayName
        })
        // reset dialog and close
        this.cancelDialog()
      } catch(e) {
        this.handleFirebaseError(e)
        return Promise.reject(e)
      }  
    },
    editPlant(plant) {
      this.plant = {
        ...plant,
        timestamp: plant.timestamp.toDate().toISOString().substr(0, 10),
        imageURLs: plant.images
      }
      this.dialog = true
    },
    removeImage(image) {
      this.plant.imageURLs = this.plant.imageURLs.filter(img => img !== image)
    },
    cancelDialog() {
      this.dialog = false
      this.plant = {
        id: null,
        name: '',
        realName: '',
        description: '',
        imageURLs: [],
        timestamp: new Date().toISOString().substr(0, 10)
      }
      this.$refs.cropper.api.remove()
    },
    slimService(formData, progress, success, failure) {
      let file = formData[0]
      // Upload file and metadata to the object 'images/mountains.jpg'
      let pathToImage = `plants/${uuid()}`
      var uploadTask = storageRef.child(pathToImage).put(file)
      // Listen for state changes, errors, and completion of the upload.
      uploadTask.on(
        'state_changed', // or 'state_changed'
        (snapshot) => {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          if (progress === 100) {
            success()
          }
        },
        (error) => {
          this.handleFirebaseError(error)
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          failure(error)
        },
        () => {
          // Upload completed successfully, now we can get the download URL
          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            this.plant.imageURLs.push(downloadURL)
            this.$refs.cropper.api.remove()
            return downloadURL
          })
        }
      )
    }
  },
  async created() {
    this.slimOptions.service = this.slimService
    try {
      db.collection("plants").onSnapshot(snapshot => {
        let plants = []
        snapshot.forEach(doc => {
          plants.push({
            id: doc.id,
            ...doc.data()
          })
        })
        this.plants = plants
      })
    } catch (e) {
      this.handleFirebaseError(e)
    }
    this.ready = true
  },
  destroyed() {
    try {
      this.$refs.cropper.destroy()
    } catch (e) {
      /*...*/
    }
  }
};
</script>
